import { useEffect, useState } from 'react';

/**
 * Subscribes to a CSS media query from JS.
 *
 * Only for cases where the difference is structural rather than cosmetic — a
 * component that renders genuinely different markup at different widths, or an
 * attribute (`<details open>`) that CSS cannot reach. Anything that is purely a
 * matter of styling belongs in a Tailwind `sm:`/`md:` variant instead, which
 * costs no JS and is correct during the first paint.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches,
  );

  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    // Re-read on subscribe: the width can have changed between the initial
    // render and this effect running.
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** Tailwind's `sm` breakpoint, which is where our mobile layouts give way. */
export const SM_QUERY = '(min-width: 640px)';
